import { useQuery } from "@apollo/client";
import { IonCol, IonGrid, IonRow } from "@ionic/react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { FormattedMessage } from "react-intl";

import Button from "@components/buttons/Button";
import ExpertAvatar from "@components/ExpertAvatar";
import OutOfOfficeForm from "@components/forms/OutOfOfficeForm";
import HandleQuery from "@components/HandleQuery";
import styles from "@components/OutOfOfficeStats.module.css";
import useNodesFromConnection from "@hooks/useNodesFromConnection";
import useOpener from "@hooks/useOpener";
import {
  GetExpertsWithOutOfOfficeSummaryDocument,
  GetExpertsWithOutOfOfficeSummaryQuery,
  GetExpertsWithOutOfOfficeSummaryQueryVariables,
  GetOutOfOfficePeriodsQuery
} from "@typing/Generated";

type ExpertType = NonNullable<
  NonNullable<NonNullable<GetExpertsWithOutOfOfficeSummaryQuery["experts"]["edges"]>[number]>["node"]
>;

type OutOfOfficePeriod = NonNullable<
  NonNullable<NonNullable<GetOutOfOfficePeriodsQuery["outOfOfficePeriods"]["edges"]>[number]>["node"]
>;

type ExpertProps = {
  expert: ExpertType;
};

type Props = {
  editingOoop: OutOfOfficePeriod | undefined;
  onEditCanceled: () => void;
};

const Expert = ({ expert }: ExpertProps) => {
  const roundStat = useCallback((value: number) => Math.round((value + Number.EPSILON) * 100) / 100.0, []);

  return (
    <IonGrid className={styles.expert}>
      <IonRow>
        <IonCol size="auto">
          <ExpertAvatar expert={expert} size={60} />
        </IonCol>
        <IonCol>
          <div className={styles.expertName}>{expert.fullName}</div>
          <div>
            <div className={styles.statBox}>
              <div className={styles.statNumber}>{roundStat(expert.outOfOfficeSummary.yearToDateDays)}</div>
              <div className={styles.statLabel}>
                <FormattedMessage id="pages.admin.outOfOffice.ytdDays" />
              </div>
            </div>
            <div className={styles.statBox}>
              <div className={styles.statNumber}>{roundStat(expert.outOfOfficeSummary.upcomingDays)}</div>
              <div className={styles.statLabel}>
                <FormattedMessage id="pages.admin.outOfOffice.upcomingDays" />
              </div>
            </div>
            <div className={styles.statBox}>
              <div className={styles.statNumber}>{roundStat(expert.outOfOfficeSummary.totalDays)}</div>
              <div className={styles.statLabel}>
                <FormattedMessage id="pages.admin.outOfOffice.totalDays" />
              </div>
            </div>
          </div>
        </IonCol>
      </IonRow>
    </IonGrid>
  );
};

const OutOfOfficeStats = ({ editingOoop, onEditCanceled }: Props) => {
  const formOpener = useOpener();

  const [localEditingOoop, setLocalEditingOoop] = useState<OutOfOfficePeriod>();

  const getExpertsQuery = useQuery<
    GetExpertsWithOutOfOfficeSummaryQuery,
    GetExpertsWithOutOfOfficeSummaryQueryVariables
  >(GetExpertsWithOutOfOfficeSummaryDocument);

  const experts = useNodesFromConnection(getExpertsQuery.data?.experts);

  const asiaExperts = useMemo(() => experts.filter(expert => expert.coversAsia), [experts]);
  const europeExperts = useMemo(() => experts.filter(expert => expert.coversEurope), [experts]);

  useEffect(() => {
    if (editingOoop) {
      setLocalEditingOoop(editingOoop);
      formOpener.open();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editingOoop]);

  const handleCreateOoop = useCallback(() => {
    onEditCanceled();
    setLocalEditingOoop(undefined);
    formOpener.open();
  }, [formOpener, onEditCanceled]);

  const handleEditCanceled = useCallback(() => {
    onEditCanceled();
    formOpener.close();
  }, [formOpener, onEditCanceled]);

  return (
    <>
      {formOpener.isOpen ? (
        <OutOfOfficeForm
          onCancel={handleEditCanceled}
          onSuccess={handleEditCanceled}
          outOfOfficePeriod={localEditingOoop}
        />
      ) : (
        <Button expand="block" onClick={handleCreateOoop}>
          <FormattedMessage id="pages.admin.outOfOffice.addOutOfOffice" />
        </Button>
      )}
      <div className={styles.statsBox}>
        <div className={styles.centerLabel}>
          <FormattedMessage id="pages.admin.outOfOffice.expertTeams" />
        </div>
        <div className={`${styles.coversBadge} ${styles.coversEuropeBadge}`}>
          <FormattedMessage id="dictionary.europe" />
        </div>
        <HandleQuery query={getExpertsQuery}>
          {_data => (
            <>
              {europeExperts.map(expert => (
                <Expert expert={expert} key={expert.id} />
              ))}
            </>
          )}
        </HandleQuery>
        <div className={`${styles.coversBadge} ${styles.coversAsiaBadge}`}>
          <FormattedMessage id="dictionary.asia" />
        </div>
        <HandleQuery query={getExpertsQuery}>
          {_data => (
            <>
              {asiaExperts.map(expert => (
                <Expert expert={expert} key={expert.id} />
              ))}
            </>
          )}
        </HandleQuery>
        <div className={styles.disclaimer}>
          <FormattedMessage id="pages.admin.outOfOffice.disclaimer" />
        </div>
      </div>
    </>
  );
};

export default OutOfOfficeStats;
